import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

interface ToastProps {
    message: string;
    type?: 'error' | 'success' | 'info';
    duration?: number;
    onClose: () => void;
}

const slideIn = keyframes`
    from {
        transform: translate(-50%, 100%);
        opacity: 0;
    }
    to {
        transform: translate(-50%, 0);
        opacity: 1;
    }
`;

const slideOut = keyframes`
    from {
        transform: translate(-50%, 0);
        opacity: 1;
    }
    to {
        transform: translate(-50%, 100%);
        opacity: 0;
    }
`;

const ToastContainer = styled.div<{ isClosing?: boolean }>`
    position: fixed;
    bottom: ${({ theme }) => theme?.spacing?.large || '1.5rem'};
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    max-width: 90%;
    width: 400px;
    
    ${({ isClosing }) => css`
        animation: ${isClosing ? slideOut : slideIn} 0.3s ease-in-out;
    `}
`;

const ToastContent = styled.div<{ type: 'error' | 'success' | 'info' }>`
    padding: ${({ theme }) => theme?.spacing ? `${theme.spacing.medium} ${theme.spacing.large}` : '1rem 1.5rem'};
    border-radius: ${({ theme }) => theme?.border?.radius?.medium || '0.5rem'};
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    
    ${({ type, theme }) => {
        switch (type) {
            case 'error':
                return css`
                    background-color: ${theme?.palette?.backgroundError?.light || '#FFE8EB'};
                    color: ${theme?.palette?.textError?.light || '#A81E35'};
                `;
            case 'success':
                return css`
                    background-color: ${theme?.palette?.backgroundSuccess?.light || '#DCFCE7'};
                    color: ${theme?.palette?.textSuccess?.light || '#137044'};
                `;
            case 'info':
            default:
                return css`
                    background-color: ${theme?.palette?.backgroundInformation?.light || '#DBF7FF'};
                    color: ${theme?.palette?.textInformation?.light || '#00639E'};
                `;
        }
    }}
`;

const Message = styled.span`
    flex: 1;
    font-size: ${({ theme }) => theme?.typography?.body02?.fontSize || '0.875rem'};
    line-height: ${({ theme }) => theme?.typography?.body02?.lineHeight || '1.25rem'};
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    padding: ${({ theme }) => theme?.spacing?.twoExtraSmall || '0.25rem'};
    margin-left: ${({ theme }) => theme?.spacing?.small || '0.75rem'};
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s;
    color: currentColor;
    
    &:hover {
        opacity: 1;
    }
    
    svg {
        width: ${({ theme }) => theme?.spacing?.medium || '1rem'};
        height: ${({ theme }) => theme?.spacing?.medium || '1rem'};
    }
`;

const Toast: React.FC<ToastProps> = ({ 
    message, 
    type = 'info', 
    duration = 5000, 
    onClose 
}) => {
    const [isClosing, setIsClosing] = React.useState(false);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, duration);
        
        return () => clearTimeout(timer);
    }, [duration]);
    
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 300);
    };
    
    return (
        <ToastContainer isClosing={isClosing}>
            <ToastContent type={type}>
                <Message>{message}</Message>
                <CloseButton onClick={handleClose} aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </CloseButton>
            </ToastContent>
        </ToastContainer>
    );
};

export default Toast;